import { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import context, { useCart, useDeliveryDate, useMeals, useRegion, useSelection, useSettings, useShipping } from './context';
import { Grid, Sidecart, Catfish, Selection } from './components';
import Toasts, { Provider as ToastsProvider } from '@root/lib/components/Toasts';
import { usePrompts } from '@root/lib/components/Prompt';
import { BOX_ID } from '@root/lib/constants';
import * as MiddlewareService from '@root/lib/services/MiddlewareService';
import * as LogicService from '@root/lib/services/LogicService';
import * as StorageService from '@root/lib/services/StorageService';
import * as PREService from '@root/lib/services/PREService';
import * as SubscriptionsService from '@root/lib/services/SubscriptionsService';
import * as StorefrontApiService from '@root/lib/services/StorefrontApiService';
import subscriptionSession from '@root/lib/classes/SubscriptionSession';
import Portal from '@root/lib/components/Portal';
import DeliveryOptionsHeader from '@root/lib/components/DeliveryOptionsHeader';
import useBoolean from '@root/lib/hooks/useBoolean';
import useAsyncCallback from '@root/lib/hooks/useAsyncCallback';
import useAsyncEffect from '@root/lib/hooks/useAsyncEffect';
import CustomerNotLoggedInError from '@root/lib/errors/CustomerNotLoggedInError';
import dayjs from '@root/lib/packages/dayjs';
import Spinner from '@root/lib/components/Spinner';
import Attention from '@root/lib/components/Icons/Attention';
import './MyChoice.scss';

const serializeLineItems = (lineItems) => {
  const variantIds = new Set();
  for (const item of lineItems) {
    variantIds.add(`${item.variant.id}`);
  }

  return [...variantIds].sort().join('|');
}

const MyChoice = ({
  boxSku,
}) => {
  const [ meals, mealsApi ] = useMeals();
  const [ region, setRegion ] = useRegion();
  const [ deliveryDate ] = useDeliveryDate();
  const [ settings ] = useSettings();
  const [ cart, cartApi ] = useCart();
  const [ cartOpen, openCart, closeCart ] = useBoolean(false);
  const [ selection ] = useSelection();
  const [ , setShipping ] = useShipping();

  // Merging selection data with time slot data
  const selectedItems = useMemo(() => {
    const timeSlot = mealsApi.getTimeSlot();
    return selection?.line_items?.map(li => {
      const boxProduct = timeSlot?.box_products?.find(bp => bp.id === li.box_product_id);
      return {
        boxProductId: li.box_product_id,
        quantity: li.choice_quantity,
        productId: parseInt(boxProduct.platform_product_id),
        variantId: parseInt(boxProduct.platform_variant_id),
      };
    });
  }, [mealsApi.getTimeSlot(), selection]);

  /**
   * Asks the user if they wish to set the delivery date which may remove items from their
   * cart before calling the setDeliveryDate function
   * 
   * @type {(date: Date) => void}
   */

  // Getting meals when zip and/or delivery date changes
  const mealFetchStatus = useRef(false);
  useAsyncEffect(async (isCancelled) => {
    if (settings.isReturn) return;
    if (region === null || deliveryDate === null) {
      mealsApi.setMealsWithTimeSlot([], null);
      return;
    }

    // Getting meals
    try {
      mealFetchStatus.current = true;
      mealsApi.setLoading(true);
      const isLocal = region.zone === 'local';
      const [ originalMeals, timeSlot ] = await mealsApi.getMeals(BOX_ID, deliveryDate, isLocal);
      if (isCancelled()) return;

      const meals = await PREService.processMeals(originalMeals);
      if (isCancelled()) return;

      mealsApi.setMealsWithTimeSlot(meals, timeSlot);
    } finally {
      if (isCancelled()) return;
      mealFetchStatus.current = false;
      mealsApi.setLoading(false);
    }
  }, [region?.zone, deliveryDate, settings.isReturn]);

  // Removing items from the cart that do not exist in meals
  useAsyncEffect(async () => {
    if (settings.isReturn) return; // Meals never change so cart can never have items that are not in meals
    if (mealFetchStatus.current) return; // If meals are being fetched we will wait for the next trigger of this effect before evicting items
    let toRemove = [];

    for (const item of cart.lineItems ?? []) {
      const found = meals.some(meal => meal.variants.some(v => v.id === item.variant.id));
      !found && toRemove.push(item.id);
    }

    toRemove = toRemove.map(id => ({id, quantity: 0}));
    await cartApi.updateItems(...toRemove);
  }, [meals, serializeLineItems(cart.lineItems ?? []), settings.isReturn, region?.zipcode]);

  // Getting cart on load and applying a discount if one is on the subscription
  useAsyncEffect(async (isCancelled) => {
    if (selection) return;
    const discountCode = settings.subscription?.discount_code;
    const query = new URLSearchParams(location.search);

    
    // Removing the discount code from the cart if on return flow, in processing window
    // and the cart already exists. Discount code might not be applied but it takes 1 request
    // to remove it and 1 request to check if it is so we might as well just make 1 request to remove it
    if (settings.isReturn && settings.inProcessingWindow && cart.id) {
      await StorefrontApiService.removeCheckoutDiscountCode(cart.id, ['id']);
      if (isCancelled()) return;
    }

    await (!cart.id ? cartApi.init() : cartApi.hydrate());
    if (isCancelled()) return;

    // Not adding any discount codes if we're in the processing window
    if (settings.isReturn && settings.inProcessingWindow) return;

    if (query.has('discount')) {
      await cartApi.setDiscountCode(query.get('discount'));
    } else if (discountCode) {
      await cartApi.setDiscountCode(discountCode);
    }
  }, []);

  // Getting shipping rules when region changes
  useAsyncEffect(async (isCancelled) => {
    if (!region?.zipcode) {
      setShipping(ps => ({
        ...ps,
        loading: false,
        rules: [],
        price: NaN,
      }));
      return;
    }

    setShipping(ps => ({...ps, loading: true}));
    try {
      const { shipping_rules, shipping_price } = await MiddlewareService.getShippingRules(boxSku, region.zipcode);
      !isCancelled() && setShipping(ps => ({
        ...ps,
        price: shipping_price,
        rules: shipping_rules,
        loading: false,
      }));

    } catch (e) {
      console.error(e);
      !isCancelled() && setShipping(ps => ({
        ...ps,
        loading: false,
      }));
    }
  }, [region?.zipcode, boxSku]);

  // Updating the cart when the region changes
  useAsyncEffect(async (isCancelled) => {
    if (!cart?.id || cart?.shippingAddress?.zip === region?.zipcode) return;
    cartApi.updateShippingAddress({
      zip: region?.zipcode,
      country: 'United States',
      address1: '123 Fake St',
      firstName: 'Fake',
      lastName: 'Name',
      city: 'Fake City',
    });
  }, [region?.zipcode, cart?.id]);

  return !selection ? (
    <div className="MyChoice MyChoice--picking">
      {!settings.isReturn && (
        <Portal element=".delivery-header">
          <DeliveryOptionsHeader
            selectedDeliveryDate={deliveryDate}
            setRegion={setRegion}
            region={region}
            mode="MC"
          />
        </Portal>
      )}
      <Grid />
      <Sidecart
        open={cartOpen}
        onClose={closeCart}
      />
      <Catfish
        itemCount={cart.itemCount ?? 0}
        onOpen={openCart}
      />
      <Toasts />
    </div>
  ) : (
    <div className="MyChoice MyChoice--picked">
      <Selection
        deliveryDate={deliveryDate}
        items={selectedItems}
      />
    </div>
  );
}

/**
 * @param {{
 *   boxSku: string;
 *   settings: {
 *     sectionSorting: string;
 *   };
 * }}
 */
export default ({
  boxSku,
  settings,
}) => {
  const initChain = useRef(Promise.resolve());
  const [ initialized, setInitialized ] = useState(false);
  const [ pendingSubscription, setPendingSubscription ] = useState(false);
  const [ , { prompt } ] = usePrompts();
  const [ contextState, setContextState ] = useState({ 
    selection: null,
    meals: {
      loading: false,
      timeSlot: null,
      state: [],
    },
    region: StorageService.getRegion(),
    deliveryDate: null,
    cart: {
      pending: [],
      originalState: {},
      state: {},
    },
    shipping: {
      loading: true,
      price: NaN,
      rules: [],
    },
    settings: {
      loading: true,
      state: {
        ...settings,
        isReturn: false,
        sectionSorting: settings.sectionSorting.split('\n'),
        deliveryDays: [],
        minMeals: Infinity,
        maxMeals: Infinity,
        mealMealsFreeShipping: Infinity,
        minDollarsFreeShipping: Infinity,
      },
    },
  });
  const contextStateRef = useRef(contextState);

  /**
   * Does the following to the cart:
   * - Adjusts prices with PRE
   * = Applies Shopify discounts on top of PRE pricing
   * - Gets shipping
   *
   * @param {string} zip
   * @param {Object} cart
   */
  const processCart = useCallback(async (cart) => {
    const preCart = await BOLD.pre.processCart({
      'token': 'abc',
      'items': cart.lineItems.map(item => ({
        id: item.variant.id,
        variant_id: item.variant.id,
        key: item.id,
        quantity: item.quantity,
        price: item.unitPrice,
        line_price: item.unitPrice * item.quantity,
        product_id: item.variant.product.id,
      })),
    });

    // Gluing PRE and Shopify data together
    let fixedCart = {
      ...cart,
      total: preCart.total_price,
      subtotal: preCart.sub_total,
      itemCount: cart.lineItems.reduce((a, i) => a + i.quantity, 0),
      lineItems: cart.lineItems.map(item => {
        const preItem = preCart.items.find(i => i.key === item.id);
        return {
          ...item,
          unitPrice: preItem.price,
          linePrice: preItem.line_price,
          variant: {
            ...item.variant,
            price: preItem.price,
          },
        };
      }),
    }
    // Applying discounts on top of PRE pricing
    fixedCart = LogicService.reapplyDiscountApplications(fixedCart);

    return fixedCart;
  }, []);

  /**
   * Sets the context state and stores state in a ref variable so function can use it without
   * causing rerenders from useEffects
   */
  const setContextStateProxy = useCallback((stateOrFn) => {
    setContextState(ps => {
      const state = typeof stateOrFn === 'function' ? stateOrFn(ps) : stateOrFn;
      contextStateRef.current = state;
      return state;
    });
  }, []);

  /**
   * Gets the meal plan and delivery date for the provided region
   * 
   * @type {(
   *   region: {
   *     zipcode: string,
   *   },
   * ) => {
   *   deliverDate: dayjs.Dayjs,
   *   mealPlan: {
   *     min_meals: number,
   *     max_meals: number,
   *     min_meals_free_shipping: number,
   *     min_dollars_free_shipping: number,
   *     delivery_days: string[],
   *   },
   * }} 
   */
  const getMealPlanAndDeliveryDateForRegion = useAsyncCallback(async (isCancelled, region) => {
    const [ time, mealPlan ] = await Promise.all([
      MiddlewareService.getTime(),
      MiddlewareService.getMealPlan(region.zipcode, boxSku),
    ]);
    if (isCancelled()) return;

    const deliveryDate = LogicService.getFirstAvailableDeliveryDay(mealPlan.delivery_days, region.transit_time, dayjs.unix(time));
    return {
      deliveryDate,
      mealPlan,
    }
  }, [boxSku]);

  /**
   * Sets the region and gets delivery day for the provided region
   * 
   * @type {(
   *  region: Object
   * ) => Promise<void>}
   */
  const setRegionProxy = useAsyncCallback(async (isCancelled, region) => {
    const prevRegion = contextStateRef.current.region;

    // Only ask if the user wants to set the region if the region is already set
    if (prevRegion && prevRegion.zipcode !== region) {
      let [ response ] = prompt({
          icon: <Attention />,
          content: <span><strong>Are you sure? </strong><br /> Changing your zip code will remove meals from your cart to ensure quantities are available.</span>,
          options: [
            {id: 'change', content: 'I\'m Sure'},
            {id: 'cancel', content: 'No', secondary: true},
          ],
      });
      response = await response.catch(() => 'cancel');
      if (response === 'cancel' || isCancelled()) return;
    }
    
    const { deliveryDate, mealPlan } = await getMealPlanAndDeliveryDateForRegion(region);
    !isCancelled() && setContextStateProxy(ps => ({
      ...ps,
      region: region,
      deliveryDate,
      settings: {
        ...ps.settings,
        loading: false,
        state: {
          ...ps.settings.state,
          deliveryDays: mealPlan.delivery_days,
          minMeals: mealPlan.min_meals,
          maxMeals: mealPlan.max_meals,
          mealMealsFreeShipping: mealPlan.min_meals_free_shipping,
          minDollarsFreeShipping: mealPlan.min_dollars_free_shipping,
        },
      },
    }));
  }, [prompt]);
  /**
   * Hydrates the context state from localStorage. Also gets the meals if the region
   * is set and flow is not return
   *
   * @type {(isReturn: boolean) => void}
   */
  const hydrateFromStorage = useCallback(async (isReturn) => {
    const customerId = Shopify.customer?.id;
    const state = StorageService.getMyChoiceState(customerId, isReturn);
    if (state === null) return;
    const { cartId, deliveryDate } = state;
    let region = null;

    // Getting zip from query and falling back to zip from storage when not return
    // flow. Return flow will get the zip/region from the subscription
    const params = new URLSearchParams(location.search);
    const queryZip = params.get('zip');
    const zip = queryZip ?? state.zip;
    if (zip && !isReturn) {
      region = await MiddlewareService.getRegion(zip);

      // If the zip from storage is not the same as the one from the
      // query, we discard the state from storage and just set the region.
      // If the zips are the same we proceed with setting all the state from
      // storage we have
      if (queryZip !== null && queryZip !== state.zip) {
        PREService.setOrderDataZip(zip);
        StorageService.setRegion(region);
        setContextStateProxy(ps => ({
          ...ps,
          region,
        }));
        return;
      }
    }

    setContextStateProxy(ps => {
      const newState = {...ps};

      if (cartId) {
        newState.cart = {
          ...newState.cart,
          state: {
            id: cartId,
          },
        };
      }

      // Not setting certain state when in return flow since the subscription data
      // will set some of the state for us
      if (!isReturn) {
        if (deliveryDate) newState.deliveryDate = dayjs.utc(deliveryDate, 'YYYY-MM-DDTHH:mm:ss[Z]').toDate();
        if (region) newState.region = region;
      }

      return newState;
    });
  }, []);

  // Saving changes to localStorage
  useEffect(() => {
    if (!initialized) return;
    const customerId = Shopify.customer?.id;
    const isReturn = contextState.settings.state.isReturn;
    const state = {
      cartId: contextState.cart.state?.id,
    };

    // only saving certain state if initial flow since return flow
    // locks setting region and the like
    if (!isReturn) {
      state.zip = contextState.region?.zipcode;
      state.deliveryDate = !contextState.deliveryDate
        ? null
        : dayjs(contextState.deliveryDate).tz('UTC').format('YYYY-MM-DDTHH:mm:ss[Z]');
    }

    StorageService.setMyChoiceState(state, customerId, isReturn);
  }, [initialized, contextState]);

  // Getting customer subscription information if the customer is logged in
  useAsyncEffect(async (isCancelled) => {
    let resolve, reject,promise = new Promise((res, rej) => {resolve = res; reject = rej});
    const prevPromise = initChain.current;
    initChain.current = promise;

    try {
      await prevPromise;
      if (isCancelled()) return;

      let pending = false;
      let wasPending = false;
      let subscription, futureOrder, deliveryDate, timeSlot, selection, region;
      const sess = subscriptionSession;
      const inProcessingWindowPromise = LogicService.isInProcessingWindow();

      do {
        const data = (await SubscriptionsService.getCompleteSubscriptionInformation(sess, isCancelled)) ?? {};
        if (isCancelled()) return;
        subscription = data.subscription;
        futureOrder = data.futureOrder;
        deliveryDate = data.deliveryDate;
        timeSlot = data.timeSlot;
        selection = data.selection;
        region = data.region;

        // No need to continue checking if the subscription was found
        if (subscription) break;

        pending = await MiddlewareService.checkForPendingSubscription();
        if (isCancelled()) return;
        if (pending) {
          !wasPending && setPendingSubscription(true);
          wasPending = true;
          await new Promise(r => setTimeout(r, 5000));
        }
      } while (pending); 

      if (!subscription || !subscription.line_items[0].sku.startsWith('my-choice')) {
        await hydrateFromStorage(false);
        if (isCancelled()) return;
        setContextStateProxy(ps => ({
          ...ps,
          settings: {
            ...ps.settings,
            state: {
              ...ps.settings.state,
              incompatSubscription: subscription?.line_items[0]?.sku?.startsWith('my-choice') === false,
            },
          },
        }));
        return;
      }

      // Redirecting if subscription is skipped
      if (futureOrder?.is_skipped) {
        location.href = '/account/#/mymenu';
        return;
      }

      // Only getting meals if there is no selection since a selection would disallow
      // the user from selecting meals
      PREService.setOrderDataZip(subscription.shipping_address.zip);
      let meals = [];
      if (!selection) {
        meals = await LogicService.getMealsForTimeSlot(timeSlot);
        if (isCancelled()) return;
        meals = await PREService.processMeals(meals);
        if (isCancelled()) return;
      }
      
      await hydrateFromStorage(true);
      if (isCancelled()) return;
      const inProcessingWindow = await inProcessingWindowPromise;
      setContextStateProxy(ps => ({
        ...ps,
        region,
        deliveryDate: deliveryDate.toDate(),
        selection,
        meals: {
          ...ps.meals,
          state: meals,
          timeSlot: timeSlot,
        },
        settings: {
          ...ps.settings,
          loading: false,
          state: {
            ...ps.settings.state,
            subscription,
            futureOrderDate: futureOrder?.date,
            isReturn: true,
            inProcessingWindow,
          },
        },
      }));
    } catch (e) {
      // If customer is not logged in, continue in initial flow mode
      if (e instanceof CustomerNotLoggedInError) {
        await hydrateFromStorage(false);
        return;
      }

      console.error(e);
      reject(e);
      return;
    } finally {
      resolve();
    }
  }, []);

  useAsyncEffect(async (isCancelled) => {
    let resolve, reject, promise = new Promise((res, rej) => {resolve = res; reject = rej});
    const prevPromise = initChain.current;
    initChain.current = promise;

    try {
      await prevPromise;
      const region = contextStateRef.current.region;
      if (isCancelled() || !region) return;
      const { mealPlan, deliveryDate } = await getMealPlanAndDeliveryDateForRegion(region);
      !isCancelled() && setContextStateProxy(ps => ({
        ...ps,
        deliveryDate: ps.settings.state.isReturn ? ps.deliveryDate : deliveryDate,
        settings: {
          ...ps.settings,
          loading: false,
          state: {
            ...ps.settings.state,
            deliveryDays: mealPlan.delivery_days,
            minMeals: mealPlan.min_meals,
            maxMeals: mealPlan.max_meals,
            mealMealsFreeShipping: mealPlan.min_meals_free_shipping,
            minDollarsFreeShipping: mealPlan.min_dollars_free_shipping,
          },
        },
      }));
    } catch (e) {
      reject(e);
    } finally {
      resolve();
    }
  }, []);

  // Setting initialized to true when the init chain is done
  useAsyncEffect(async (isCancelled) => {
    await initChain.current;
    !isCancelled() && setInitialized(true);
  }, []);

  return (
    <ToastsProvider>
      {initialized ? (
        <context.Provider value={{
          state: contextState,
          stateRef: contextStateRef,
          setState: setContextStateProxy,
          functions: {
            processCart,
            setRegionProxy,
          },
        }}>
          <MyChoice boxSku={boxSku} />
        </context.Provider>
      ) : (
        <div className="MyChoice MyChoice--loading">
          <Spinner />
          {pendingSubscription && (
            <div style={{
              textAlign: 'center',
              fontSize: '18px',
              fontWeight: 'bold',
              maxWidth: '400px',
              margin: '0 auto',
              lineHeight: 'normal',
            }}>
              Hang on, you’ve recently placed a subscription order and it may take a few moments to set up everything before you pick your meals again.
            </div>
          )}
        </div>
      )}
    </ToastsProvider>
  );
};
